import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'

import { NumberInput, SelectField } from '../shared'
import {
    selectElecConsumption,
    selectElecRate,
    selectElectricEstimated,
    selectGasConsumption,
    selectGasRate,
    selectGasEstimated,
    selectTariffId,
    selectSelfConsumption,
    setElecConsumption,
    setElecRate,
    setElectricEstimated,
    setGasConsumption,
    setGasRate,
    setGasEstimated,
    setSelectedTariff,
    setTariffId,
    setSelfConsumption
} from '../../store/slices/energySlice'
import { selectTariffs } from '../../store/slices/catalogSlice'

// Self consumption options
const SELF_CONSUMPTION_OPTIONS = [
    { value: '', label: 'Select' },
    { value: 'mdg003_homeallday', label: 'Home All Day' },
    { value: 'mdg003_homehalfday', label: 'Home Half Day' },
    { value: 'mdg003_outallday', label: 'Out All Day' }
]

// Styles
const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2
    },
    row: {
        display: 'flex',
        gap: 3,
        flexWrap: 'wrap'
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flex: 1,
        minWidth: 200
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        mb: 1
    },
    inputRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 1
    },
    checkbox: {
        fontSize: '12px'
    },
    tariffsSection: {
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        mt: 1
    }
}

const EnergyTab = () => {
    const dispatch = useDispatch()

    // Selectors
    const elecConsumption = useSelector(selectElecConsumption)
    const elecRate = useSelector(selectElecRate)
    const electricEstimated = useSelector(selectElectricEstimated)
    const gasConsumption = useSelector(selectGasConsumption)
    const gasRate = useSelector(selectGasRate)
    const gasEstimated = useSelector(selectGasEstimated)
    const tariffId = useSelector(selectTariffId)
    const selfConsumption = useSelector(selectSelfConsumption)
    const tariffs = useSelector(selectTariffs)

    // Handlers
    const handleElecConsumptionChange = (value) => {
        dispatch(setElecConsumption(value))
    }

    const handleElecRateChange = (value) => {
        dispatch(setElecRate(value))
    }

    const handleElectricEstimatedChange = (event) => {
        dispatch(setElectricEstimated(event.target.checked))
    }

    const handleGasConsumptionChange = (value) => {
        dispatch(setGasConsumption(value))
    }

    const handleGasRateChange = (value) => {
        dispatch(setGasRate(value))
    }

    const handleGasEstimatedChange = (event) => {
        dispatch(setGasEstimated(event.target.checked))
    }

    const handleTariffChange = (value) => {
        const selectedTariff = tariffs.find(t => t.tariff_id === value)
        if (selectedTariff) {
            dispatch(setSelectedTariff(selectedTariff))
            dispatch(setTariffId(selectedTariff.tariff_id))
        } else {
            dispatch(setSelectedTariff({}))
            dispatch(setTariffId(0))
        }
    }

    const handleSelfConsumptionChange = (value) => {
        dispatch(setSelfConsumption(value))
    }

    // Format tariff options for select
    const tariffOptions = tariffs.map(tariff => ({
        value: tariff.tariff_id,
        label: `${tariff.tariff} dr:${tariff.day_unit_rate}p ${tariff.night_unit_rate ? 'nr: ' + tariff.night_unit_rate + 'p' : 'nr: n.a'} sc:${tariff.standing_charge}p`
    }))

    return (
        <Box sx={styles.container}>
            {/* Electricity & Gas Section */}
            <Box sx={styles.row}>
                {/* Electricity */}
                <Box sx={styles.section}>
                    <Typography sx={styles.sectionTitle}>Electricity</Typography>
                    <Box sx={styles.inputRow}>
                        <NumberInput
                            label="Consumption (kWh/p.a)"
                            value={elecConsumption}
                            onChange={handleElecConsumptionChange}
                            suffix="kWh"
                            decimalScale={0}
                            sx={{ flex: 1 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={electricEstimated}
                                    onChange={handleElectricEstimatedChange}
                                />
                            }
                            label={<Typography sx={styles.checkbox}>estimated</Typography>}
                        />
                    </Box>
                    <NumberInput
                        label="Rate (p/kWh)"
                        value={elecRate}
                        onChange={handleElecRateChange}
                        suffix="p"
                        decimalScale={2}
                    />
                </Box>

                {/* Gas */}
                <Box sx={styles.section}>
                    <Typography sx={styles.sectionTitle}>Gas</Typography>
                    <Box sx={styles.inputRow}>
                        <NumberInput
                            label="Consumption (kWh/p.a)"
                            value={gasConsumption}
                            onChange={handleGasConsumptionChange}
                            suffix="kWh"
                            decimalScale={0}
                            sx={{ flex: 1 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={gasEstimated}
                                    onChange={handleGasEstimatedChange}
                                />
                            }
                            label={<Typography sx={styles.checkbox}>estimated</Typography>}
                        />
                    </Box>
                    <NumberInput
                        label="Rate (p/kWh)"
                        value={gasRate}
                        onChange={handleGasRateChange}
                        suffix="p"
                        decimalScale={2}
                    />
                </Box>
            </Box>

            {/* Tariffs Section */}
            {tariffs && tariffs.length > 0 && (
                <Box sx={styles.tariffsSection}>
                    <SelectField
                        label="New Supplier / Rate"
                        value={tariffId}
                        onChange={handleTariffChange}
                        options={tariffOptions}
                        sx={{ minWidth: 280 }}
                    />
                    <SelectField
                        label="Self Consumption"
                        value={selfConsumption}
                        onChange={handleSelfConsumptionChange}
                        options={SELF_CONSUMPTION_OPTIONS}
                        sx={{ minWidth: 160 }}
                    />
                </Box>
            )}
        </Box>
    )
}

export default EnergyTab
